/* eslint-disable */
/*
 * This class represents a component for the 360 photo viewer.
 *
 *  // 'renderCanvas' is the id of the canvas element that will be
 *  // used by the viewer
 *	let player = createPlayer('renderCanvas');
 *	// This can be a link to the JSON or JSON data itself, either
 *	// as a string or as an object.
 *	player.load('http://some_path/panoData.json');
 *
 * Created by Oleg Samus, oleg@modsy.com, September 2022.
 * 
 * Copyright (c) 2022 End 2 End, Inc. All rights reserved.
 */

import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import Utils3D from 'shared/libraries/mwc/3d/utils/utils3D';

// These are for the map
import Utils2D from 'shared/libraries/mwc/utils/utils2D';
import CameraDir from './web/assets/camera_dir.svg';
import EnvMap from 'shared/assets/mainEnvironment.env';
import GeoUtils from 'shared/libraries/mwc/3d/nodeGraph/geoUtils';
import Spline from 'shared/libraries/mwc/3d/nodeGraph/spline';
import Extrude from 'shared/libraries/mwc/3d/extrude';
import AssetCacher, { ASSET_TYPE } from '../../shared/libraries/mwc/3d/nodeGraph/assetCacher';
// TODO: These two needs to be moved to shared.
import AssetManager from '../home-designer/Controllers/AssetManager';
import ParametricFixtureAssetProvider from '../home-designer/Controllers/ParametricFixtureAssetProvider';
import {forEach} from "jszip";
import {ResultAssetResolution, ResultAssetType} from "shared/libraries/backend/ResultAsset";

// If true, linked cameras are shown with a semi-transparent
// white ring within the perspective view.
const SHOW_LINKED_CAMERAS = true;

// If true, linked cameras are shown with a semi-transparent
// white ring within the perspective view.
const SHOW_CURSOR = true;

// If true, we load a low-res version of the pano before loading
// the high-res one. This is meant to speed up transitions, but
// at this point high-res versions are loading quickly enough
// to probably not need this.
const USE_LOWRES_VERSIONS = false;

// If true, we pre-load low-res panoramas for every linked camera
// in the current view. This only works if USE_LOWRES_VERSIONS is
// true as well. Also likely not needed right now.
const PRELOAD_LINKED_TEXTURES = true;

const LOWRES_SCALE_MULT = 0.40;
let FULLRES_SCALE_MULT = 0.882;
let JPEG_QUALITY = 85;
let DO_OPTIMIZE = 'true';

// Transition animation duration.
const TRANSITION_ANIMATION_SECONDS = 0.75;

// Motion blur for when the user is looking around.
const NORMAL_MOTION_BLUR = 0.5;
// Motion blur for transitions.
const TRANSITION_MOTION_BLUR = 10.0;

// Color and opacity for the cursor ring
const CURSOR_COLOR = new BABYLON.Color4(1, 1, 1, 0.5);
// Color and opacity for linked camera rings
const CAMERA_POSITION_COLOR = new BABYLON.Color4(1, 1, 1, 0.50);
const CAMERA_POSITION_COLOR_HOVERED = new BABYLON.Color4(1, 1, 1, 1);
//const CAMERA_POSITION_COLOR = new BABYLON.Color4(1, 0, 0, 1);

// Panning speed - the higher, the lower it actually is
const PANNING_SPEED_DIVISOR = 3000;

const DEFAULT_CAMERA_BETA = 90/180*Math.PI;
const DOME_SIZE = 1000;
const COORDS_SCALE = DOME_SIZE/12;
const MAX_INTERSPHERE_LENGTH = 10.0;
const CAMERA_TARGET_DIST = 1;
let USE_CUBE_MAP = true;

const ENABLE_TOPDOWN_MAP = false;

const FOV_360 = 64.17127;

const AnimationType =
{
	LINEAR:			0,
	EASE_IN_OUT:	1
}

/*
TODO:
- Support properly getting the data in
- Support multiple floors
*/
class MapView
{
	constructor(strCanvasId, parentPlayer)
	{
		this.setupMapScene(strCanvasId);
		this.parentPlayer = parentPlayer;
	}

	setupMapScene(mapCanvasId)
	{
		this.mapCanvas = document.getElementById(mapCanvasId);
		this.mapCanvas.style.display = 'block';
		this.mapCanvas.addEventListener('pointerdown',  (evt) => {  this.onMouseDown(evt); });
		this.mapCanvas.addEventListener('pointermove',  (evt) => {  this.onMouseMove(evt); });
		this.mapCanvas.addEventListener('pointerup',  (evt) => {  this.onMouseUp(evt); });

        this.mapEngine = new BABYLON.Engine(this.mapCanvas, true, { preserveDrawingBuffer: true, stencil: true });
		this.mapEngine.renderEvenInBackground = false;
		this.mapEngine.setHardwareScalingLevel(1.0);

		this.mapScene = new BABYLON.Scene(this.mapEngine);
		this.mapScene.clearColor = new BABYLON.Color4(0, 0, 0, 0.15);
		this.mapScene.ambientColor = new BABYLON.Color3(0, 0, 0);
		this.mapScene.detachControl();
		
        this.mapCamera = new BABYLON.FreeCamera('mapCamera', BABYLON.Vector3.Zero(), this.mapScene);
        this.mapCamera.minZ = 0.1;

		this.mapCamera.position = new BABYLON.Vector3(0, 100, 0);
        this.mapCamera.mode = BABYLON.Camera.ORTHOGRAPHIC_CAMERA;
        this.mapCamera.fovMode = BABYLON.Camera.FOVMODE_VERTICAL_FIXED;

		this.setMapZoom(14, 14);
		this.mapScene.activeCamera = this.mapCamera;

		let lightDir = new BABYLON.Vector3(-1, -1.5, 1);
		lightDir.normalize();
		const light = new BABYLON.DirectionalLight('DirectionalLight', lightDir, this.mapScene);
		light.intensity = 9.0;
		light.diffuse = new BABYLON.Color3(255/255, 240/255, 194/255);

		light.specular = new BABYLON.Color3(0, 0, 0);
        light.autoUpdateExtends = true;
        light.autoCalcShadowZBounds = true;

        const hdrTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(EnvMap, this.mapScene);
        this.mapScene.environmentIntensity = 0.4;
        this.mapScene.environmentTexture = hdrTexture;

        this.shadowGenerator = new BABYLON.ShadowGenerator(1024, light);
		this.shadowGenerator.setDarkness(0.40);
        this.shadowGenerator.useContactHardeningShadow = true;
        this.shadowGenerator.contactHardeningLightSizeUVRatio = 0.18;
/*
		this.defaultPipeline = new BABYLON.DefaultRenderingPipeline('default', false, this.mapScene, [this.mapCamera]);
		this.defaultPipeline.fxaaEnabled = true;
		this.defaultPipeline.sharpenEnabled = true;
		this.defaultPipeline.sharpen.edgeAmount = 0.5;
		this.defaultPipeline.imageProcessing.contrast = 1.2;
*/
        // Help with self-shadowing
        this.shadowGenerator.bias = 0.0001;

		this.mapEngine.runRenderLoop(() => { 
			this.mapScene.render();
		});

		this.mapWallsMaterial = new BABYLON.PBRMaterial('wallMat', this.mapScene);
		this.mapWallsMaterial.albedoColor = new BABYLON.Color3(63/255, 62/255, 78/255);
		this.mapWallsMaterial.emissiveColor = this.mapWallsMaterial.albedoColor.scale(0.10);
		this.mapWallsMaterial.environmentIntensity = 0.0;
		this.mapWallsMaterial.freeze();

		this.mapFloorMaterial = new BABYLON.PBRMaterial('floorMat', this.mapScene);
		this.mapFloorMaterial.albedoColor = new BABYLON.Color3(195/255, 195/255, 207/255);
		this.mapFloorMaterial.freeze();

		this.cameraMaterial = new BABYLON.StandardMaterial(`mapCameraMaterial`, this.mapScene);
		this.cameraMaterial.diffuseColor = new BABYLON.Color3(1,1,1);
		this.cameraMaterial.emissiveColor = this.cameraMaterial.diffuseColor.scale(1);
		this.cameraMaterial.alpha = 0.55;

		this.selectedCameraMaterial = new BABYLON.StandardMaterial(`mapSelectedCameraMaterial`, this.mapScene);
		this.selectedCameraMaterial.diffuseColor = new BABYLON.Color3(0, 0, 0);
		this.selectedCameraMaterial.emissiveColor = new BABYLON.Color3(0, 126/255, 230/255);
		this.selectedCameraMaterial.alpha = 1.0;

        const texture = new BABYLON.Texture(CameraDir, this.mapScene);
		this.cameraDirMaterial = new BABYLON.StandardMaterial('mapCameraDir', this.mapScene);
        this.cameraDirMaterial.specularColor = BABYLON.Color3.Black();
        this.cameraDirMaterial.diffuseTexture = texture;
        this.cameraDirMaterial.opacityTexture = texture;

		const scale = 0.0008;
        this.cameraDirMesh = BABYLON.MeshBuilder.CreatePlane(`mapCameraDirMesh`, { width: 300*scale, height: 350*scale }, this.mapScene);
        this.cameraDirMesh.material = this.cameraDirMaterial;
        this.cameraDirMesh.rotation.x = Math.PI/2;
		this.cameraDirMesh.position = new BABYLON.Vector3(0, 0, 0.10);

		window.assetManager = new AssetManager();
		window.parametricFixtureAssetProvider = new ParametricFixtureAssetProvider();
	}

	setMapZoom(fWidth, fHeight)
	{
		const aspectRatio = this.mapCanvas.clientWidth/this.mapCanvas.clientHeight;
		let orthoW, orthoH;
		if(fWidth > fHeight)
		{
			orthoW = fWidth/2;
			orthoH = orthoW/aspectRatio;
		}
		else
		{
			orthoH = fHeight/2;
			orthoW = orthoH*aspectRatio;
		}

        this.mapCamera.orthoTop = orthoH;
        this.mapCamera.orthoBottom = -orthoH;
        this.mapCamera.orthoLeft = -orthoW;
        this.mapCamera.orthoRight = orthoW;
		this.mapCamera.rotation.x = Math.PI/2;
	}

	createTopDownMap(normaJSON, panoData, currCameraInfo)
	{
		// Clear previous meshes
		for(let idx = this.mapScene.meshes.length - 1; idx >= 0; idx--)
		{
			let currMesh = this.mapScene.meshes[idx];
			if(currMesh.mapMesh)
				currMesh.dispose();
		}

		AssetCacher.getInstance().ensureAssetsLoaded(normaJSON['scene']['usedAssets']['materialIds'], this.mapScene, () => {
			let roomWallMeshes = [];
			const wallThickness = normaJSON['scene']['wallThickness'];
			for(let roomInfo of normaJSON['scene']['rooms'])
			{
				let rootVolume = roomInfo['volumes'].filter(volumeInfo => volumeInfo.anchor == null)[0];
				if(!rootVolume)
					continue;

				let floorMaterialAssignment = rootVolume.attachedObjects.filter(ao => ao.type == 'materialAssignment' && ao.anchor.semanticType == 0)[0];
				let matId = floorMaterialAssignment?.materialId;

				let sourceSpline = new Spline(rootVolume.sourceSpline);
				let thickenedPoints = sourceSpline.createThickPolyline(wallThickness).points;
				let thickenedSpline =  new Spline(thickenedPoints, true);
				let center = thickenedSpline.getCenter();
				thickenedSpline.projectOntoPlane(BABYLON.Vector3.Up());

				const WALL_HEIGHT = 1.78;

				const options = {
					shape: thickenedSpline.getLinearPoints(),
					path: new Spline([BABYLON.Vector3.Zero(), new BABYLON.Vector3(0, WALL_HEIGHT, 0)]),
					updatable: false,
					finalMeshAlign: 'toOrigin',
					cap: BABYLON.Mesh.CAP_ALL
				};
				let roomWalls = Extrude.createMesh(`room${roomInfo.id}Walls`, options, this.mapScene);
				roomWalls.rotation.x = Math.PI;
				roomWalls.rotation.y = -Math.PI/2;
				roomWalls.position = center;
				roomWalls.material = this.mapWallsMaterial;
				roomWalls.mapMesh = true;
				this.shadowGenerator.getShadowMap().renderList.push(roomWalls);
				roomWallMeshes.push(roomWalls);

				let flooRpolygon = BABYLON.Mesh.CreatePolygon(`room${roomInfo.id}Floor`, sourceSpline.getLinearPoints3D(), this.mapScene, null, false, BABYLON.Mesh.DOUBLESIDE);
				flooRpolygon.material = this.mapFloorMaterial;
				flooRpolygon.mapMesh = true;

				let pCachedMaterialMesh = AssetCacher.getInstance().getCachedAsset(ASSET_TYPE.MATERIAL, matId, this.mapScene);
				if(pCachedMaterialMesh) {
					flooRpolygon.material = pCachedMaterialMesh.material;
					flooRpolygon.material.metallicReflectanceTexture = null;
				}		
				GeoUtils.applyBoxUVsToMeshes([flooRpolygon], 0, 0, 0);
				flooRpolygon.receiveShadows = true;

				// See if we can get doors
				let doorInfos = rootVolume.attachedObjects.filter(ao => {
					return ao.type == 'fixture' && ao.affectedRoomIds?.length > 1 && ao.parameters?.find(v => v.n == 'width') && ao['depth'] > 0.0;
				});
				for(let doorInfo of doorInfos)
				{
					let width = doorInfo.parameters.find(v => v.n == 'width')?.sv;
					if(width >= 0) {
						let pBox = BABYLON.MeshBuilder.CreateBox('tempBox', { 
							width: width, 
							depth: doorInfo['depth'], 
							height: WALL_HEIGHT*2,
							sideOrientation: BABYLON.Mesh.FRONTSIDE,
							updatable: false
						}, this.mapScene);

						// Vec
						let anchorPos = new BABYLON.Vector3(doorInfo['anchor']['point'].x, doorInfo['anchor']['point'].y, doorInfo['anchor']['point'].z);
						let dirVec = new BABYLON.Vector3(doorInfo['direction'].x, doorInfo['direction'].y, doorInfo['direction'].z);
						pBox.position = anchorPos.add(dirVec.scale(-doorInfo['depth']/2));
						pBox.rotationQuaternion = Utils3D.directionToQuaternion(dirVec);
					}
				}
			}

			// Create camera positions
			for(let cameraInfo of panoData['cameras'])
			{
				// Note that the name has to match the camera name exactly
				let cameraObj = Player.createCameraPositionObject(cameraInfo.name, this.cameraMaterial, this.mapScene);
				cameraObj.position = new BABYLON.Vector3(cameraInfo['position']['x'], cameraInfo['position']['y'], cameraInfo['position']['z']);
				cameraObj.scaling = new BABYLON.Vector3(3.5, 3.5, 3.5);
				cameraObj.mapMesh = true;
				cameraObj.isCamera = true;
			}

			let bbox = Utils3D.getSceneBBox(this.mapScene);
			let bboxSize = bbox.maximumWorld.subtract(bbox.minimumWorld);
			bboxSize.x += 2;
			bboxSize.z += 2;
			this.setMapZoom(bboxSize.x, bboxSize.z);

			this.onCameraChanged(currCameraInfo);

		});
	}

	onCameraRotated(degAngle)
	{
		if(!this.cameraDirMesh.parent)
			return;

		let yAxisRot = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), degAngle/180*Math.PI + Math.PI/2);
		this.cameraDirMesh.parent.rotationQuaternion = yAxisRot;
	}

	onCameraChanged(cameraInfo)
	{
		let cameraMeshes = this.mapScene.meshes.filter(mesh => mesh.isCamera);
		cameraMeshes.forEach(cameraMesh => {
			if(cameraMesh.name == cameraInfo?.name) {
				cameraMesh.material = this.selectedCameraMaterial;
				cameraMesh.renderOutline = true;
				cameraMesh.outlineWidth = 0.01;
				cameraMesh.outlineColor = new BABYLON.Color4(1,1,1,0.5);
				this.cameraDirMesh.parent = cameraMesh;
			}
			else {
				cameraMesh.renderOutline = false;
				cameraMesh.material = this.cameraMaterial;
			}
		});
	}

	getSceneCoordsFromEvent(evt)
	{
		let rect = this.mapCanvas.getBoundingClientRect();
		let fScaling = this.mapEngine.getHardwareScalingLevel();
		let fPtX = (evt.clientX - rect.left)/fScaling;
		let fPtY = (evt.clientY - rect.top)/fScaling;
		return new BABYLON.Vector2(fPtX, fPtY);
	}

	getCameraObjectAtPosition(vecCoords)
	{
		let pickInfo = this.mapScene.pickWithBoundingInfo(vecCoords.x, vecCoords.y, (mesh) => {
			if(!mesh.isCamera)
				return false;
			return true;
		});

		return pickInfo.pickedMesh;
	}

	onMouseMove(event)
	{
		event.stopPropagation();
		if(this.isMouseDown)
			return;

		let vecMouseUpPos = this.getSceneCoordsFromEvent(event);
		let pickedCamera = this.getCameraObjectAtPosition(vecMouseUpPos);
		if(pickedCamera)
			this.mapCanvas.style.cursor = 'pointer';
		else
			this.mapCanvas.style.cursor = 'default';
	}

	onMouseDown(event)
	{
		event.stopPropagation();
		this.isMouseDown = true;
		this.mouseDownCanvasPos = this.getSceneCoordsFromEvent(event);
	}

	onMouseUp(event)
	{
		event.stopPropagation();
		this.isMouseDown = false;

		let vecMouseUpPos = this.getSceneCoordsFromEvent(event);
		if(this.mouseDownCanvasPos && BABYLON.Vector2.Distance(vecMouseUpPos, this.mouseDownCanvasPos) < 5)
		{
			// Click
			let pickedCamera = this.getCameraObjectAtPosition(vecMouseUpPos);
			if(pickedCamera)
				this.parentPlayer.moveToCamera(pickedCamera.name);
		}
	}
}

export default class Player
{
	constructor(strCanvasId, options = {})
	{
		this.panoData = {};
		this.currCameraInfo = null;
		
		this.options = options;
		if(this.options['showLinkedCameras'] === undefined)
			this.options['showLinkedCameras'] = SHOW_LINKED_CAMERAS;

		if(this.options['showHotspots'] === undefined)
			this.options['showHotspots'] = false;

		if(this.options['hotspotSize'] === undefined)
			this.options['hotspotSize'] = 28;

		if(this.options['showCursor'] === undefined)
			this.options['showCursor'] = SHOW_CURSOR;

		const queryParams = this.queryLocationString();
		if(queryParams['debugMode'] === 'true')
		{
			this.options['showLinkedCameras'] = true;
			this.options['showHotspots'] = true;
			this.options['showCursor'] = true;
			this.debugMode = true;
		}

		if(queryParams['useCubeMap'])
			USE_CUBE_MAP = queryParams['useCubeMap'] === 'true';

		if(queryParams['fullSize'] === 'true')
		{
			FULLRES_SCALE_MULT = 1.0;
			JPEG_QUALITY = 100;
			DO_OPTIMIZE = 'false';
		}

		if(queryParams['scale'] !== undefined)
		{
			FULLRES_SCALE_MULT = queryParams['scale'];
		}

		if(queryParams['quality'] !== undefined)
		{
			JPEG_QUALITY = queryParams['quality'];
		}

		this.linkedCamerasObjects = [];
		this.hotspots = [];
		this.hoveredHotspot = null;

		this.isHardwareAccelerated = true;

        this.canvas = document.getElementById(strCanvasId);

		this.cachedCameraPosMaterials = {};

		this.currDome = null;
		this.nextDome = null;

		this.isInitializing = false;
		this.onAfterInitializeCameraMove = null;

		this.setup3DEngine();
		if(ENABLE_TOPDOWN_MAP)
			this.mapView = new MapView('topdownMapCanvas', this);

		this.cursorObject = null;
		this.isMoving = false;
		this.hoveredCameraMesh = null;

		this.originalCachedMaterialAlphas = {};
		this.materialAlphaAnimInterval = null;

		window.addEventListener("resize",  () => {  this.onWindowResize(); });
		this.canvas.addEventListener("pointermove",  (evt) => {  this.onMouseMove(evt); });
		this.canvas.addEventListener("pointerdown",  (evt) => {  this.onMouseDown(evt); });	
		this.canvas.addEventListener("pointerup",  (evt) => {  this.onMouseUp(evt); });	

		window.addEventListener("keydown", (evt) => { this.onKeyDown(evt); });

		this.canvas.addEventListener("drop",  (evt) => {  this.receiveFile(evt); });	
		this.canvas.addEventListener("dragover",  (evt) => {  this.onDragOver(evt); });	

		this.isMouseDown = false;
		this.mouseDownCanvasPos = null;

		this.onHotspotClicked = null;
		this.onCameraNavigated = null;

		if(queryParams['url'])
			this.load(queryParams['url']);

		this.isDragging = false;
		this.dragSequence = '';
		this.mParticleLogEvent = (eventName, eventAttributes) => {};
	}

	setup3DEngine()
	{
        this.engine = new BABYLON.Engine(this.canvas, true, {  preserveDrawingBuffer: true, stencil: true });
		this.engine.renderEvenInBackground = false;
		this.engine.setHardwareScalingLevel(1.0);

		this.isHardwareAccelerated = this.engine.getGlInfo().renderer.indexOf('SwiftShader') < 0;

		this.scene = new BABYLON.Scene(this.engine);
		this.scene.clearColor = new BABYLON.Color3(1, 1, 1);
		this.scene.ambientColor = new BABYLON.Color3(0, 0, 0);

		let camera = new BABYLON.ArcRotateCamera('mainCamera', 0, 0, CAMERA_TARGET_DIST, new BABYLON.Vector3(0, 0, 0), this.scene);
		camera.attachControl(this.canvas, true, false);

		camera.inputs.remove(camera.inputs.attached['mousewheel']);
		camera.inputs.attached['keyboard'].keysUp = [];
		camera.inputs.attached['keyboard'].keysDown = [];
		camera.inputs.attached['pointers'].buttons = [0];
		camera.inputs.attached['pointers'].pinchZoom = false;
		camera.inputs.attached['pointers'].multiTouchPanAndZoom = false;
		camera.inputs.attached['pointers'].multiTouchPanning = false;

		if(this.mapView)
		{
			camera.onViewMatrixChangedObservable.add(() => {
				let ray = this.scene.activeCamera.getForwardRay();
				ray.direction.y = 0;
				ray.direction.normalize();
				let degAngle = Utils2D.getAngleFromPositiveX(ray.direction.x, ray.direction.z);
				this.mapView.onCameraRotated(degAngle);
			});
		}

		// Invert controls
		camera.inputs.attached.pointers.angularSensibilityX = -PANNING_SPEED_DIVISOR;
		camera.inputs.attached.pointers.angularSensibilityY = -PANNING_SPEED_DIVISOR;

		this.scene.activeCamera = camera;
		this.scene.activeCamera.beta = DEFAULT_CAMERA_BETA;
		this.scene.activeCamera.alpha = -Math.PI/2.0;

		camera.fov = FOV_360/180.0*Math.PI;
		camera.minZ = 0.1;

		// We need the pipeline in order to enable AA with the motion blur
		if(this.isHardwareAccelerated)
		{
			this.defaultPipeline = new BABYLON.DefaultRenderingPipeline('default', false, this.scene, [camera]);
			this.defaultPipeline.fxaaEnabled = true;
			this.defaultPipeline.sharpenEnabled = true;
			this.defaultPipeline.sharpen.edgeAmount = 0.5;
			// this.defaultPipeline.imageProcessing.contrast = 1.2;

			this.motionBlur = new BABYLON.MotionBlurPostProcess('mainMB', this.scene, 1.0, camera);
			this.motionBlur.motionStrength = NORMAL_MOTION_BLUR;
		}
		else
		{
			this.engine.setHardwareScalingLevel(1.25);
			this.scene.activeCamera.inertia *= 0.75;

			const warningElem = document.createElement('div');
			warningElem.innerText = "Hardware acceleration disabled - navigation will be slow";
			warningElem.style = 'position: absolute;left: 4px; bottom: 4px; color: #ff7800; font-weight: 100;';
			this.canvas.parentElement.appendChild(warningElem);
		}
		this.engine.runRenderLoop(() => { 
			this.scene.render();
		});

		this.guiScale = 1;
		
		this.uiManager = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI('layerMain', true, BABYLON.UtilityLayerRenderer.DefaultUtilityLayer.utilityLayerScene, BABYLON.Texture.BILINEAR_SAMPLINGMODE, true);
		this.uiManager.renderScale = this.guiScale;

		this.scene.onBeforeCameraRenderObservable.add(() => {
			this.repositionHotspots();
		});

		this.currDome = this.createDome('currDome');
		this.nextDome = this.createDome('nextDome');
		this.nextDome.setEnabled(false);

		this.envLayer = new BABYLON.Layer('','', this.scene, true);
		this.envLayer.isBackground = true;
	}

	reset(bKeepCurrDome = false)
	{
		this.onAfterInitializeCameraMove = null;

		this.clearLinkedCameraObjects();
		this.clearHotspots();

		if(!bKeepCurrDome && this.currDome && this.currDome.material.diffuseTexture)
		{
			let oldTexture = this.currDome.material.diffuseTexture;
			this.currDome.material.diffuseTexture = null;
			oldTexture.dispose();
		}				
	}

	load(strUrlOrData, optCallback = null, logEvent = (eventName, eventAttributes) => {})
	{
		// See what kind of object this is:
		if(typeof strUrlOrData === 'object')
			this.initializePano(strUrlOrData, optCallback);
		else
		{
			try
			{
				// Try to parse it as JSON
				this.initializePano(JSON.parse(strUrlOrData), optCallback);
			}
			catch(err)
			{
				// If not, this must be a URL:
				fetch(strUrlOrData)
					.then((data) => { return data.json(); })
					.then((jsonData) =>
					{
						this.initializePano(jsonData, optCallback);
					});

			}
		}
		this.mParticleLogEvent = logEvent;
	}

	onWindowResize()
	{
		this.engine.resize();
	}

	getCameraInfo(strName)
	{
		return this.panoData['cameras'].filter((info) => { return info.name === strName; })[0];
	}

	getPropInfo(strId)
	{
		return this.panoData['props'].filter((info) => { return info.id === strId; })[0];
	}

	createDome(strName)
	{
		let domeMesh;
		if(USE_CUBE_MAP)
		{
			const iMapColumns = 4;
			const iMapRows = 3;
			const uvPadding = 0.0;

			let faceUVs = new Array(6);

			let vecBack = new BABYLON.Vector4(0, 1/iMapRows + uvPadding, -1/iMapColumns, 2/iMapRows - uvPadding);
			let vecRight = new BABYLON.Vector4(-1/iMapColumns, 1/iMapRows + uvPadding, -2/iMapColumns, 2/iMapRows - uvPadding);
			let vecLeft = new BABYLON.Vector4(-3/iMapColumns, 1/iMapRows + uvPadding, -4/iMapColumns, 2/iMapRows - uvPadding);
			let vecFront = new BABYLON.Vector4(-2/iMapColumns, 1/iMapRows + uvPadding, -3/iMapColumns, 2/iMapRows - uvPadding);

			// Left
			faceUVs[3] = vecLeft;
			// Front
			faceUVs[0] = vecFront;
			// Back
			faceUVs[1] = vecBack;
			// Right
			faceUVs[2] = vecRight;

			// up
			faceUVs[4] = new BABYLON.Vector4(1/iMapColumns + uvPadding, 3/iMapRows - uvPadding, 2/iMapColumns - uvPadding, 2/iMapRows);
			// down
			faceUVs[5] = new BABYLON.Vector4(1/iMapColumns + uvPadding, 1/iMapRows, 2/iMapColumns - uvPadding, 0 + uvPadding);
    
			const options = {
				width: DOME_SIZE,
				depth: DOME_SIZE,
				height: DOME_SIZE,
				faceUV: faceUVs,
				wrap: true,
				sideOrientation: BABYLON.Mesh.BACKSIDE
			};

			domeMesh = BABYLON.MeshBuilder.CreateBox(strName, options, this.scene);
		}
		else
		{
			domeMesh = BABYLON.MeshBuilder.CreateSphere(strName, { 
				diameter: DOME_SIZE, 
				segments: 96, 
				sideOrientation: BABYLON.Mesh.BACKSIDE
			}, this.scene);
		}

		domeMesh.rotation.y = Math.PI/2.0;
		domeMesh.material = new BABYLON.StandardMaterial(`${strName}Material`, this.scene);
		domeMesh.material.diffuseColor = new BABYLON.Color3(1,1,1);
		domeMesh.material.emissiveColor = domeMesh.material.diffuseColor.clone();

		return domeMesh;
	}

	queryLocationString()
	{
		// This function is anonymous, is executed immediately and
		// the return value is assigned to QueryString!
		let query_string = {};
		let query = window.location.search.substring(1);
		let vars = query.split("&");
		for(let i = 0; i < vars.length; i++)
		{
			let pair = vars[i].split("=");
			if(typeof query_string[pair[0]] === "undefined")
				query_string[pair[0]] = decodeURIComponent(pair[1]);
			else if(typeof query_string[pair[0]] === "string")
			{
				let arr = [query_string[pair[0]], decodeURIComponent(pair[1])];
				query_string[pair[0]] = arr;
			}
			else
				query_string[pair[0]].push(decodeURIComponent(pair[1]));
		}
		return query_string;
	}

	onDomeTextureLoaded(diffuseTexture)
	{
		diffuseTexture.vScale = -1.0;
		if(USE_CUBE_MAP)
			diffuseTexture.wrapV = BABYLON.Texture.MIRROR_ADDRESSMODE;
	}

	loadTexture(dome, cameraInfo, bDelayFullResLoad, optCallback)
	{
		let texture = cameraInfo['image'];
		let lowresSrc = this.getImageSource(texture, LOWRES_SCALE_MULT, LOWRES_SCALE_MULT);
		let highResSrc = this.getImageSource(texture, FULLRES_SCALE_MULT, FULLRES_SCALE_MULT);

		if (this.hasAssets()) {
			const cameraName = cameraInfo['name'];
			lowresSrc = Player.getCubemapUrl(this.panoData['assets'], cameraName, ResultAssetResolution.K1);
			highResSrc = Player.getCubemapUrl(this.panoData['assets'], cameraName, ResultAssetResolution.K6);
		}

		if(!USE_LOWRES_VERSIONS)
			lowresSrc = highResSrc;
		
		if(cameraInfo['preloadLowResTexture'])
		{
			dome.material.diffuseTexture = cameraInfo['preloadLowResTexture'];
			// Avoid having it be deleted later
			delete cameraInfo['preloadLowResTexture'];
		}
		else
			dome.material.diffuseTexture = new BABYLON.Texture(lowresSrc, this.scene, { noMipmap: false }, true, BABYLON.Texture.TRILINEAR_SAMPLINGMODE);

		this.onDomeTextureLoaded(dome.material.diffuseTexture);
		dome.needsCallbackCall = true;

		let cameraPosition = cameraInfo['position'];
		let vecNextCameraPos = new BABYLON.Vector3(cameraPosition.x, cameraPosition.y, cameraPosition.z);
		dome.position = vecNextCameraPos.clone();

		if(optCallback)
		{
			if(dome.material.diffuseTexture.isReadyOrNotBlocking())
			{
				delete dome.needsCallbackCall;
				optCallback(vecNextCameraPos);
			}
			else
			{
				dome.material.diffuseTexture.onLoadObservable.addOnce(() => { 
					delete dome.needsCallbackCall; 
					optCallback(vecNextCameraPos);
				});
			}
		}

		let funcOnHighresLoad = () => {
			
			let oldTexture = dome.material.diffuseTexture;

			dome.material.diffuseTexture = dome.material.diffuseTextureHighRes;
			this.onDomeTextureLoaded(dome.material.diffuseTexture);

			delete dome.material.diffuseTextureHighRes;
			oldTexture.dispose();		
			
			if(dome.needsCallbackCall)
			{
				delete dome.needsCallbackCall;
				optCallback(vecNextCameraPos);
			}
		};

		// We do this since if loading happens during the animation, it will be jerky
		if(USE_LOWRES_VERSIONS)
		{
			let loadDelay = bDelayFullResLoad ? TRANSITION_ANIMATION_SECONDS : 0;
			setTimeout(() => {
				dome.material.diffuseTextureHighRes = new BABYLON.Texture(highResSrc, this.scene);
				if(dome.material.diffuseTextureHighRes.isReadyOrNotBlocking())
					funcOnHighresLoad();
				else
					dome.material.diffuseTextureHighRes.onLoadObservable.addOnce(funcOnHighresLoad);
			}, loadDelay*1000);
		}
	}

	static createCameraPositionObject(strName, material, scene)
	{
		const INNER_RADIUS = 0.25;
		const THICKNESS = 0.14;
		const myShape = [
				new BABYLON.Vector3(INNER_RADIUS, 0, 0),
				new BABYLON.Vector3(INNER_RADIUS + THICKNESS, 0, 0),
				new BABYLON.Vector3(INNER_RADIUS + THICKNESS, 0.0001, 0),
				new BABYLON.Vector3(INNER_RADIUS, 0.0001, 0)
			];
	
		const options = {
			shape: myShape,
			radius: INNER_RADIUS,
			tessellation: 64,
		};

		let newObject = BABYLON.MeshBuilder.CreateLathe(strName, options, scene);
		newObject.renderingGroupId = 1;
		newObject.material = material;
		return newObject;
	}

	createCursorObject(strName, color)
	{
		const INNER_RADIUS = 0.25;
		const THICKNESS = 0.05;
		const myRingShape = [
				new BABYLON.Vector3(INNER_RADIUS, 0, 0),
				new BABYLON.Vector3(INNER_RADIUS + THICKNESS, 0, 0),
				new BABYLON.Vector3(INNER_RADIUS + THICKNESS, 0.0001, 0),
				new BABYLON.Vector3(INNER_RADIUS, 0.0001, 0)
			];
		const myArrowShape = [
			new BABYLON.Vector3(-THICKNESS * 0.8, 0, 1.8 * THICKNESS),
			new BABYLON.Vector3(0, 			 0, 1.8 * THICKNESS + THICKNESS),
			new BABYLON.Vector3(THICKNESS * 0.8, 	 0, 1.8 * THICKNESS),
		];

		const options = {
			shape: myRingShape,
			radius: INNER_RADIUS,
			tessellation: 64,
		};
		const optionsCenter = {
			radius: INNER_RADIUS/5,
			tessellation: 64
		};

		const optionsRing = {
			shape: myArrowShape
		};

		let ring = BABYLON.MeshBuilder.CreateLathe(strName, options, this.scene);
		let centerCircle = BABYLON.MeshBuilder.CreateDisc(strName, optionsCenter, this.scene);
		let arrow = BABYLON.MeshBuilder.CreatePolygon(strName, optionsRing, this.scene);
		centerCircle.rotation.x = Math.PI/2;
		ring.renderingGroupId = 1;
		centerCircle.renderingGroupId = 1;

		const material = this.getColorMaterial(color);
		ring.material = material;
		centerCircle.material = material;
		arrow.material = material;

		const finalMesh = BABYLON.Mesh.MergeMeshes([ring, centerCircle, arrow]);
		finalMesh.renderingGroupId = 1;

		return finalMesh;
	}

	initializePano(jsonObject, optCallback = null)
	{
		if(this.isInitializing)
			console.warn(`VTPLAYER: Second initializePano() called before the first one finished. Results could be unpredictable.`);

		this.reset();

		this.isInitializing = true;
		this.panoData = jsonObject;

		if(this.mapView)
		{
			// TEMP:
			const FILE_URL = '';
			fetch(FILE_URL).then(response => response.json()).then(data => {
				this.mapView.createTopDownMap(data, this.panoData, this.currCameraInfo);
			});
		}

		// Look for our first view
		let cameraInfo = this.getCameraInfo(this.panoData['startCameraName']);
		this.currCameraInfo = cameraInfo;
		if(this.mapView)
			this.mapView.onCameraChanged(cameraInfo);

		this.log(`Begin initialization with start camera ${cameraInfo['name']}`);

		// Hide the next dome for now
		this.nextDome.position.y = DOME_SIZE*100;

		// Load the texture from the first camera:
		this.loadTexture(this.currDome, cameraInfo, false, (vecNextCameraPos) => {
			this.scene.activeCamera.setTarget(vecNextCameraPos.clone(), false, true, true);
			this.updateLinkedCameras();
			this.updateHotspots();

			const fExtraAngle = cameraInfo['rotation'] ? cameraInfo['rotation']/180*Math.PI : 0.0;
			this.scene.activeCamera.alpha = -Math.PI/2 - fExtraAngle;

			this.isInitializing = false;
			this.log(`Finished initialization with start camera ${cameraInfo['name']}`);

			if(this.onAfterInitializeCameraMove)
				this.moveToCameraInternal(this.onAfterInitializeCameraMove.cameraInfo, this.onAfterInitializeCameraMove.bResetToOriginalRotation);
			this.onAfterInitializeCameraMove = null;
		});

		// No cursor on mobile; also note that it's very slow when doing constant
		// intersection tests on mobile.
		if(this.getDeviceType() === 'desktop' && this.options['showCursor'] && !this.cursorObject)
		{
			this.cursorObject = this.createCursorObject('cursor', CURSOR_COLOR);
			this.cursorObject.scaling.scaleInPlace(0.5);
		}

		if(optCallback)
			optCallback();
	}

	getSceneCoordsFromEvent(evt)
	{
		let rect = this.canvas.getBoundingClientRect();
		// let fScaling = this.engine.getHardwareScalingLevel();
		let fPtX = (evt.clientX - rect.left);
		let fPtY = (evt.clientY - rect.top);
		return new BABYLON.Vector2(fPtX, fPtY);
	}

	onMouseDown(event)
	{
		this.isMouseDown = true;
		this.mouseDownCanvasPos = this.getSceneCoordsFromEvent(event);
		if(this.cursorObject)
			this.cursorObject.visibility = 0;
	}

	onMouseUp(event)
	{
		this.isMouseDown = false;

		if(this.hoveredHotspot)
			return;

		if(this.cursorObject && this.options['showCursor'])
			this.cursorObject.visibility = 1;

		let vecMouseUpPos = this.getSceneCoordsFromEvent(event);
		if(this.mouseDownCanvasPos && BABYLON.Vector2.Distance(vecMouseUpPos, this.mouseDownCanvasPos) < 5)
			this.onMouseClick(vecMouseUpPos);

		if (this.isDragging) {
			this.mParticleLogEvent(
				'Mouse Drag',
				{ startEnd: this.dragSequence + `X[${event.clientX}]Y[${event.clientY}]` }
			);
			this.dragSequence = '';
			this.isDragging = false;
		} else {
			this.mParticleLogEvent('Mouse click', { clickLocation: { x: event.clientX, y: event.clientY } });
		}
	}

	onMouseMove(event)
	{
		if (this.isMouseDown && !this.isDragging) {
			this.isDragging = true;
			this.dragSequence = `X[${event.clientX}]Y[${event.clientY}]`;
		}
		if(!this.cursorObject)
			return;

		let vecCanvasPoint = this.getSceneCoordsFromEvent(event);
		let hitPoint = this.getPointOnDome(vecCanvasPoint.x, vecCanvasPoint.y, true);
		if(hitPoint && !this.isMoving) {
			this.cursorObject.position = hitPoint;
			
			const targetCameraName = this.getCameraClosestToScreenPoint(vecCanvasPoint.x, vecCanvasPoint.y)
			if(!targetCameraName) {
				return;
			}

			// Pick a point on the plane around the tharget camera to find cursor angle around it
			const pickRay = this.scene.pick(vecCanvasPoint.x, vecCanvasPoint.y).ray;
			const camHitInfo = this.getDomeHitToCamera(targetCameraName);
			const intersectionPoint = Utils3D.getLinePlaneIntersection(pickRay.origin, pickRay.origin.add(pickRay.direction.scale(3000)), new BABYLON.Vector3(0, 1, 0), camHitInfo.pickedPoint.y);
			const nextCamPos = camHitInfo.pickedPoint;
			const nextCamDirVector = nextCamPos.subtract(intersectionPoint).normalize();
			const angle = BABYLON.Vector3.GetAngleBetweenVectors(new BABYLON.Vector3(0, 0, 1), nextCamDirVector, new BABYLON.Vector3(0, 1, 0));
			this.cursorObject.rotation.y = angle;

			// Color the appropriate target camera position
			const mesh = this.linkedCamerasObjects.filter( (c) => { return c['linkedCameraName'] == targetCameraName})[0];
			if(mesh) {
				const oldMesh = this.hoveredCameraMesh;
				if(oldMesh) {
					oldMesh.material = this.getColorMaterial(CAMERA_POSITION_COLOR);
				}

				const mat = this.getColorMaterial(CAMERA_POSITION_COLOR_HOVERED);
				mesh.material = mat;
				this.hoveredCameraMesh = mesh;
			}
		}
	}

	getColorMaterial(color) {
		const strColor = color.toHexString();
		if(!this.cachedCameraPosMaterials[strColor])
		{
			let pNewMaterial = new BABYLON.StandardMaterial(`360player${strColor}Material`, this.scene);
			pNewMaterial.diffuseColor = color;
			pNewMaterial.emissiveColor = pNewMaterial.diffuseColor.scale(1);
			pNewMaterial.alpha = color.a;
			this.cachedCameraPosMaterials[strColor] = pNewMaterial;
		}
		return this.cachedCameraPosMaterials[strColor]
	}

	setNavigationElementsVisible(isVisible) {
		this.options['showLinkedCameras'] = isVisible;
		this.options['showCursor'] = isVisible;
		this.cursorObject.visibility = isVisible ? 1 : 0;
		this.updateLinkedCameras();
	}

	getNavigationElementsVisible() {
		return this.options['showLinkedCameras'];
	}

	getCameraClosestToScreenPoint(iScreenX, iScreenY)
	{
		let hitPoint = this.getPointOnDome(iScreenX, iScreenY, false);
		if(!hitPoint)
			return null;

		// Otherwise, find the camera that's the closest to that point:
		let fMinDist = Number.MAX_VALUE;
		let strNextCamName = null;
		for(let mesh of this.linkedCamerasObjects)
		{
			let fCurrDist = BABYLON.Vector3.Distance(mesh.position, hitPoint);
			if(fCurrDist < fMinDist)
			{
				fMinDist = fCurrDist;
				strNextCamName = mesh.name;
			}
		}

		return strNextCamName;
	}

	log(strMessage)
	{
		if(!this.debugMode)
			return;

		console.log(`VTPLAYER: ${strMessage}`);
	}

	onMouseClick(vecCanvasPos)
	{
		if(this.isMoving)
			return;

		const strNextCamName = this.getCameraClosestToScreenPoint(vecCanvasPos.x, vecCanvasPos.y);
		this.moveToCamera(strNextCamName);
	}

	moveToCamera(cameraInfoOrName, bResetToOriginalRotation = false)
	{
		if(!cameraInfoOrName)
			return false;

		// Handle string name input
		let cameraInfo = cameraInfoOrName;
		if(typeof cameraInfoOrName === 'string')
			cameraInfo = this.getCameraInfo(cameraInfoOrName);

		// If the camera is a portal, we move to the linked camera instead
		const isFloorTransition = cameraInfo['floorTransitionCameraName'];
		if(isFloorTransition) {
			cameraInfo = this.getCameraInfo(cameraInfo['floorTransitionCameraName']);
		}

		let bRes = true;
		if(cameraInfo['scene'])
		{
			fetch(cameraInfo['scene'])
				.then((data) => { return data.json(); })
				.then((jsonData) =>
				{
					this.reset(true);
					this.panoData = jsonData;
					this.moveToCameraInternal(this.panoData['startCameraName'], true);
				});
		}		
		else
			bRes = this.moveToCameraInternal(cameraInfo, bResetToOriginalRotation, true, isFloorTransition);

		return bRes;
	}

	moveToCameraInternal(cameraInfo, bResetToOriginalRotation = false, bLoadImage = true, autoOrientCamera)
	{
		const camName = cameraInfo ? cameraInfo['name'] : null;
		if(this.isMoving || !cameraInfo)
		{
			this.log(`Move to camera ${camName} ignored isMoving = ${this.isMoving} isInitializing = ${this.isInitializing}`);
			return false;
		}

		if(this.isInitializing)
		{
			// Schedule this to run after initialization is complete
			this.log(`Move to camera ${camName} queued due to initialization in progress`);
			this.onAfterInitializeCameraMove = { cameraInfo, bResetToOriginalRotation };
			return true;
		}

		if(typeof cameraInfo === 'string')
			cameraInfo = this.getCameraInfo(cameraInfo);

		this.log(`Move to camera ${cameraInfo['name']} begin`);

		// Override this so that if our current zone is not frontyard, but the next one is,
		// we auto-rotate:
		if(this.currCameraInfo && !bResetToOriginalRotation)
		{
			const currZoneInfo = this.getZoneInfoForCamera();
			const nextZoneInfo = this.getZoneInfoForCamera(cameraInfo['name']);

			if(currZoneInfo && nextZoneInfo && currZoneInfo['type'] !== 'frontyard' && nextZoneInfo['type'] === 'frontyard')
				bResetToOriginalRotation = true;
		}

		this.engine.renderEvenInBackground = true;
		this.isMoving = true;

		// Take a screenshot to use for the transition background texture
		const TARGET_SCREENSHOT_WIDTH = 8;
		const fScreenshotHeight = Math.floor(this.canvas.height/this.canvas.width*TARGET_SCREENSHOT_WIDTH);	
		BABYLON.Tools.CreateScreenshotAsync(this.engine, this.scene.activeCamera, { width: TARGET_SCREENSHOT_WIDTH, height: fScreenshotHeight }).then((imageData) => 
		{
			this.envLayer.texture = new BABYLON.Texture(imageData, this.scene);
			this.envLayer.isEnabled = true;
		});

		if(this.motionBlur)
			this.motionBlur.motionStrength = TRANSITION_MOTION_BLUR;

		if(!bLoadImage)
			return true;

		const fps = this.isHardwareAccelerated ? 60.0 : 30.0;

		this.loadTexture(this.nextDome, cameraInfo, true, (vecNextCameraPos) => {
			let vecToTarget = vecNextCameraPos.subtract(this.currDome.position);

			this.nextDome.material.alpha = 0.0;
			this.nextDome.setEnabled(true);

			if(this.materialAlphaAnimInterval)
			{
				clearInterval(this.materialAlphaAnimInterval);
				this.materialAlphaAnimInterval = null;
			}
			else
			{
				this.originalCachedMaterialAlphas = {};
				Object.entries(this.cachedCameraPosMaterials).forEach(([key, mat]) => {
					this.originalCachedMaterialAlphas[key] = mat.alpha;
				});
			}

			Object.entries(this.cachedCameraPosMaterials).forEach(([key, mat]) => {			
				mat.alpha = 0;
			});

			// Position the dome spheres so they're overlapping by the right amount:
			let fCurrLength = vecToTarget.length();
			if(fCurrLength > MAX_INTERSPHERE_LENGTH)
				vecToTarget.scaleInPlace(MAX_INTERSPHERE_LENGTH/fCurrLength);

			let vecNextDomePos = this.currDome.position.add(vecToTarget.scale(COORDS_SCALE));
			this.nextDome.position = vecNextDomePos;	
			this.nextDome.scaling = new BABYLON.Vector3(2,2,2);

			const fExtraAngle = cameraInfo['rotation'] ? cameraInfo['rotation']/180*Math.PI : 0.0;
			let fNextAlpha = -Math.PI/2 - fExtraAngle;
			const fNextBeta = DEFAULT_CAMERA_BETA;

			this.scene.activeCamera.alpha = this.scene.activeCamera.alpha % (Math.PI * 2); // Alpha can be cranked up infinitely high otherwise
			let fCurrAlpha = this.scene.activeCamera.alpha;
			let fCurrBeta = this.scene.activeCamera.beta;

			if(autoOrientCamera) {
				const adjacentCameraInfo = this.getCameraInfo(cameraInfo.linkedCameras[0]);
				const currentCamPosition = new BABYLON.Vector3(cameraInfo.position.x, cameraInfo.position.y, cameraInfo.position.z);
				const adjacentCamPosition = new BABYLON.Vector3(adjacentCameraInfo.position.x, adjacentCameraInfo.position.y, adjacentCameraInfo.position.z);
				const nextCamDirection = adjacentCamPosition.subtract(currentCamPosition);
				const radAngleNextCam = BABYLON.Vector3.GetAngleBetweenVectors(new BABYLON.Vector3(0, 0, 1), nextCamDirection, new BABYLON.Vector3(0, 1, 0));
				fNextAlpha = (-Math.PI/2 - radAngleNextCam);
				fCurrAlpha = this.scene.activeCamera.alpha > 0 ? this.scene.activeCamera.alpha : this.scene.activeCamera.alpha + Math.PI * 2;

				// minimizing interp distance by removing negative rotations
				const rotationDistance = fNextAlpha - fCurrAlpha;
				if(Math.abs(rotationDistance) > Math.PI) {
					if(rotationDistance > 0) {
						fNextAlpha -= Math.PI *2
					}
					else {
						fCurrAlpha -= Math.PI *2
					}
				}
			}

			let animProps = {
				position: [this.currDome.position, vecNextDomePos],
				opacity: [this.currDome.material.alpha, 0],
				currOpacity: [this.currDome.material.alpha, -0.15],
				alpha: [fCurrAlpha, fNextAlpha], 
				beta: [fCurrBeta, fNextBeta]
			};
			this.animateValues(animProps, TRANSITION_ANIMATION_SECONDS, (currValuesTarget, fInterpValue, bIsDone) =>
			{
				this.currDome.material.alpha = Math.max(currValuesTarget['currOpacity'], 0.0);
				this.nextDome.material.alpha = 1.0 - currValuesTarget['opacity'];
				this.scene.activeCamera.setTarget(currValuesTarget['position'].clone(), false, true, true);
				if(bResetToOriginalRotation || autoOrientCamera)
					{
						this.scene.activeCamera.alpha = currValuesTarget['alpha'];
						this.scene.activeCamera.beta = currValuesTarget['beta'];
					}
				// this.log(`Move to camera ${cameraInfo['name']} anim tick interp = ${fInterpValue} done = ${bIsDone}`);

				if(bIsDone)
				{
					this.isMoving = false;
					this.log(`Move to camera ${cameraInfo['name']} done`);

					if(this.motionBlur)
						this.motionBlur.motionStrength = NORMAL_MOTION_BLUR;
					let prevDome = this.currDome;
					if(prevDome.material.diffuseTexture)
					{
						let oldTexture = prevDome.material.diffuseTexture;
						prevDome.material.diffuseTexture = null;
						oldTexture.dispose();
					}		

					this.materialAlphaAnimInterval = this.animateValues({opacity: [0, 1]}, TRANSITION_ANIMATION_SECONDS/2, (currValuesTarget, fInterpValue, bIsDone) =>
					{
						Object.entries(this.cachedCameraPosMaterials).forEach(([key, mat]) => mat.alpha = this.originalCachedMaterialAlphas[key]*currValuesTarget['opacity']);
						if(bIsDone)
							this.materialAlphaAnimInterval = null;
					}, undefined, fps);

					this.currDome = this.nextDome;
					this.currDome.scaling = new BABYLON.Vector3(1,1,1);
					this.currDome.material.alpha = 1.0;

					this.nextDome = prevDome;
					this.nextDome.position.y = DOME_SIZE*100;
					this.nextDome.material.alpha = 1.0;

					this.envLayer.isEnabled = false;
					let pOldTextire = this.envLayer.texture;
					this.envLayer.texture = null;
					if(pOldTextire)
						pOldTextire.dispose();

					this.scene.activeCamera.setTarget(vecNextCameraPos.clone(), false, true, true);
					this.currDome.position = vecNextCameraPos.clone();
					this.currDome.computeWorldMatrix(true);

					this.currCameraInfo = cameraInfo;
					if(this.mapView)
						this.mapView.onCameraChanged(cameraInfo);

					this.updateLinkedCameras();
					this.updateHotspots();

					this.nextDome.setEnabled(false);
					this.engine.renderEvenInBackground = false;

					if(this.onCameraNavigated)
						this.onCameraNavigated();
				}
			}, undefined, fps);
		});
		
		return true;
	}

    getAngleFromPositiveX(fX, fY)
    {
        let fAngle = Math.atan2(-fY, fX) / Math.PI * 180.0;
        if(fAngle < 0)
            fAngle += 360.0;
        return fAngle;
    }

	repositionHotspots()
	{
		for(let button of this.hotspots)
		{
			button.moveToVector3(button.requiredPos3D, this.scene);
			let newAlpha = this.isMoving ? 0 : 1;
			if(newAlpha != button.alpha)
				button.alpha = newAlpha;
		}
	}

	clearHotspots()
	{
		this.hoveredHotspot = null;
		for(let button of this.hotspots)
		{
			button.dispose();
		}

		this.hotspots = [];
	}

	setShowHotspots(bValue)
	{
		this.options['showHotspots'] = bValue;
		for(let button of this.hotspots)
			button.isVisible = this.options['showHotspots'];
	}

	updateHotspots()
	{
		this.clearHotspots();
		for(let hotspotInfo of this.currCameraInfo['hotspots'])
		{
			const fHorAngle = hotspotInfo.x*Math.PI*2.0 + Math.PI/2.0;
			const fVertAngle = hotspotInfo.y*Math.PI - Math.PI/2.0;

			// This logic is for a plain sphere
			let pMatrixY = BABYLON.Matrix.RotationY(fHorAngle);
			let pMatrixX = BABYLON.Matrix.RotationZ(-fVertAngle);
			let pMatrix = pMatrixX.multiply(pMatrixY);
			let vecRayDir = BABYLON.Vector3.TransformCoordinates(new BABYLON.Vector3(1, 0, 0), pMatrix);

			let vecCameraPos = new BABYLON.Vector3(this.currCameraInfo['position'].x, this.currCameraInfo['position'].y, this.currCameraInfo['position'].z);		
			let vecSpotPos = vecRayDir.scale(DOME_SIZE/2).add(vecCameraPos);

			let hotspotButton = BABYLONGUI.Button.CreateImageOnlyButton("but", (location.protocol + '//' + location.host + location.pathname) + "/button_v2.png");
			hotspotButton.scaleX = this.guiScale;
			hotspotButton.scaleY = this.guiScale;

			hotspotButton.isVisible = this.options['showHotspots'];

			hotspotButton.width = `${this.options['hotspotSize']}px`;
			hotspotButton.height = `${this.options['hotspotSize']}px`;
			hotspotButton.hoverCursor = 'pointer';
			hotspotButton.thickness = 0;

			let propInfo = this.getPropInfo(hotspotInfo.id);

			hotspotButton.onPointerUpObservable.add((mouseData, state) => { 
				console.log(`Button with id = ${hotspotInfo.id} clicked!`); 
				if(propInfo)
				{
					console.log(`\tname = ${propInfo.name}`); 
					console.log(`\tprice = ${propInfo.price}`); 
					console.log(`\timage = ${propInfo.image}`); 
				}

				if(this.onHotspotClicked)
				{
					this.onHotspotClicked(hotspotInfo.id, propInfo);
					// We can do something that blocks the view and the
					// hotspot leave will never get called. So we do this
					// manually.
					this.onHotspotLeave(hotspotInfo);
					// We need to stop the event propagation, otherwise, 
					// our  mouse up handler will be called after this, and will
					// think no hotspot has been clicked since we reset it above,
					// and will navigate to the closest camera.
					if(state.userInfo)
						state.userInfo.event.stopImmediatePropagation();
				}				
			});
			hotspotButton.onPointerEnterObservable.add(() => { this.onHotspotEnter(hotspotInfo); });
			hotspotButton.onPointerOutObservable.add(() => { this.onHotspotLeave(hotspotInfo); });

			this.uiManager.addControl(hotspotButton);

			hotspotButton.requiredPos3D = vecSpotPos;
			this.hotspots.push(hotspotButton);
		}
	}

	onHotspotEnter(hotspotInfo)
	{
		this.hoveredHotspot = hotspotInfo;
		if(this.cursorObject)
			this.cursorObject.visibility = 0;
	}

	onHotspotLeave(hotspotInfo)
	{
		this.hoveredHotspot = null;
		if(this.cursorObject)
			this.cursorObject.visibility = 1;
	}

	clearLinkedCameraObjects()
	{
		this.linkedCamerasObjects.forEach((mesh) => { 

			// Dispose of any preloaded textures (unless they're in our next view, too):
			let camInfo = this.getCameraInfo(mesh.name);
			if(camInfo['preloadLowResTexture'] && this.currCameraInfo['linkedCameras'].indexOf(mesh.name) < 0)
			{
				camInfo['preloadLowResTexture'].dispose();
				delete camInfo['preloadLowResTexture'];
			}

			mesh.dispose(); 
		});

		this.linkedCamerasObjects = [];
	}

	getDomeHitToCamera(cameraName) {
		const vecCurrCameraPos = new BABYLON.Vector3(this.currCameraInfo['position'].x, this.currCameraInfo['position'].y, this.currCameraInfo['position'].z);
		vecCurrCameraPos.y = 1.5;

		let camInfo = this.getCameraInfo(cameraName);

		// We need to create a ray through that point:
		let vecRealPos = new BABYLON.Vector3(camInfo['position'].x, 0, camInfo['position'].z);
		let vecDir = vecRealPos.subtract(vecCurrCameraPos);
		vecDir.normalize();

		let pRay = new BABYLON.Ray(vecCurrCameraPos, vecDir);
		let hitInfo = pRay.intersectsMesh(this.currDome, false);

		return hitInfo;
	}

	updateLinkedCameras()
	{
		this.clearLinkedCameraObjects();
		for(let strLinkedCameraName of this.currCameraInfo['linkedCameras'])
		{
			const hitInfo = this.getDomeHitToCamera(strLinkedCameraName);
			if(!hitInfo || !hitInfo.hit)
				continue;

			let camInfo = this.getCameraInfo(strLinkedCameraName);
			let camObject = Player.createCameraPositionObject(strLinkedCameraName, this.getColorMaterial(CAMERA_POSITION_COLOR), this.scene);

			const pos1 = new BABYLON.Vector3(this.currCameraInfo.position.x, this.currCameraInfo.position.y, this.currCameraInfo.position.z);
			const pos2 = new BABYLON.Vector3(camInfo.position.x, camInfo.position.y, camInfo.position.z);
			const distToCamera = pos1.subtract(pos2).length() / 4;

			camObject.position = hitInfo.pickedPoint;
			camObject.scaling.scaleInPlace(Math.min(DOME_SIZE / 1.5, DOME_SIZE / 3.5 / distToCamera));

			camObject.visibility = this.options['showLinkedCameras'] ? 1 : 0;
			camObject['linkedCameraName'] = strLinkedCameraName;

			// Preload the low-res texture on it:
			if(!camInfo['preloadLowResTexture'] && PRELOAD_LINKED_TEXTURES && USE_LOWRES_VERSIONS)
			{
				let lowresSrc = this.getImageSource(camInfo['image'], LOWRES_SCALE_MULT, LOWRES_SCALE_MULT);
				if(this.hasAssets()) {
					lowresSrc = Player.getCubemapUrl(this.panoData['assets'], strLinkedCameraName, ResultAssetResolution.K1);
				}
				camInfo['preloadLowResTexture'] = new BABYLON.Texture(lowresSrc, this.scene);
			}

			this.linkedCamerasObjects.push(camObject);
		}
	}

	getRaySphereIntersection(pRay, pSphere)
	{
        let x = pSphere.centerWorld.x - pRay.origin.x;
        let y = pSphere.centerWorld.y - pRay.origin.y;
        let z = pSphere.centerWorld.z - pRay.origin.z;
        let tc = x*pRay.direction.x + y*pRay.direction.y + z*pRay.direction.z;

		let pyth = x*x + y*y + z*z;
		//let d2 = tc*tc - pyth;
		let d2 = pyth - tc*tc;
	
		let radius2 = pSphere.radiusWorld*pSphere.radiusWorld;
		if (d2 > radius2)
			return false;

		let t1c = Math.sqrt(radius2 - d2);

		let t1 = tc - t1c;
		if(t1 < 0)
			t1 = Number.MAX_VALUE;
		let t2 = tc + t1c;
		if(t2 < 0)
			t2 = Number.MAX_VALUE;
	
		if(t1 === Number.MAX_VALUE && t2 === Number.MAX_VALUE)
			return false;

		return Math.min(t1, t2);
	}

	getPointOnDome(ptX, ptY, bScaleDist)
	{
		if(!this.currDome)
			return null;

		let pRay = this.scene.createPickingRay(ptX, ptY);
		////pRay.origin = this.scene.activeCamera.position.add(pRay.direction.scale(this.scene.activeCamera.minZ + 1));

		// This is faster than doing triangle intersection testing
		let pSphere = this.currDome.getBoundingInfo().boundingSphere;
		let res = this.getRaySphereIntersection(pRay, pSphere);
		if(res === false)
			return null;

		let fScale = bScaleDist ? 1.0/DOME_SIZE : 1;
		return pRay.origin.add(pRay.direction.scale(res*fScale));
	}

	getIsCmdOrControlDown(evt)
	{
		return evt.ctrlKey || evt.metaKey;
	}

	onKeyDown(evt)
	{
		let otherKeyValue = null;
		let isCmdOrControlDown = this.getIsCmdOrControlDown(evt);

		if(evt.which)
			otherKeyValue = String.fromCharCode(evt.which);

		if((evt.code === 'KeyW' || otherKeyValue === 'W') && evt.shiftKey)
		{
			this.scene.forceWireframe = !this.scene.forceWireframe;
		}
		else if(evt.code === 'KeyF' || otherKeyValue === 'F')
		{
			this.canvas.requestFullscreen();
		}
		else if(evt.code === 'KeyH' || otherKeyValue === 'H')
		{
			this.setShowHotspots(!this.options['showHotspots']);
		}
		else if(evt.code === 'KeyG' || otherKeyValue === 'G')
		{
			let targetScene = this.scene;
			if(isCmdOrControlDown && this.mapView)
				targetScene = this.mapView.mapScene;

			targetScene.meshes.forEach(m => Utils3D.unfreezeMaterials(m.material));

			if(targetScene.debugLayer.isVisible())
				targetScene.debugLayer.hide();
			else
				targetScene.debugLayer.show({ showExplorer: true }).then(() => {});
		}
		else if((evt.code === 'ArrowUp' || otherKeyValue === '&'))
		{			
			evt.preventDefault();
			const strNextCamName = this.getCameraClosestToScreenPoint(this.canvas.width/2.0, this.canvas.height/2.0);
			this.moveToCamera(strNextCamName);
		}
		else if((evt.code === 'ArrowDown' || otherKeyValue === '('))
		{
		}
		else if((evt.code === 'ArrowRight' || otherKeyValue === '\''))
		{
		}
		else if((evt.code === 'ArrowLeft' || otherKeyValue === '%'))
		{
		}
		else if((evt.code === 'KeyC' || otherKeyValue === 'C') && isCmdOrControlDown)
		{
			try {
				const activeCamera = this.scene.activeCamera;
				const vtContext = {
					camera: this.currCameraInfo,
					rotation: {
						alpha: activeCamera.alpha,
						beta: activeCamera.beta,
						radius: activeCamera.radius
					}
				};
				const clipboardText = JSON.stringify(vtContext);
				navigator.clipboard.writeText(clipboardText).then(() => {
					console.log('Wrote camera to clipboard.');
				});
			} catch (e) {
				console.error('Failed to write camera to clipboard.');
			}
		}
		else if((evt.code === 'KeyV' || otherKeyValue === 'V') && isCmdOrControlDown)
		{
			try {
				navigator.clipboard.readText().then((clipboardText) => {
					const vtContext = JSON.parse(clipboardText);
					const cameraInfo = vtContext.camera;
					const rotation = vtContext.rotation;
					this.moveToCameraInternal(cameraInfo, false, false);
					const activeCamera = this.scene.activeCamera;
					activeCamera.alpha = rotation.alpha;
					activeCamera.beta = rotation.beta;
					activeCamera.radius = rotation.radius;
					console.log('Read camera from clipboard.');
				});
			} catch (e) {
				console.error('Failed to read camera from clipboard.');
			}
		}
	}

	interpolate(t, a, b)
	{
		return (1 - t) * a + t * b;
	}

	convertLinearToEaseInOutInterpolation(fInterp)
	{
		if(fInterp <= 0.5)
			return 2.0*fInterp*fInterp;

		fInterp -= 0.5;
		return 2.0*fInterp*(1.0 - fInterp) + 0.5;
	}

	animateValues(propValueMap, fDurationSeconds, pCallback, eAnimationType = undefined, fps = 60.0)
	{
		let tempObj = {};
		let finalState = {};
		for(let key in propValueMap)
		{
			tempObj[key] = propValueMap[key][0];
			finalState[key] = propValueMap[key][1];
		}

		return this.animateObjectProperties(tempObj, finalState, fDurationSeconds, pCallback, eAnimationType, true, fps);
	}

	animateObjectProperties(pObject, propValueMap, fDurationSeconds, pCallback, eAnimationType = undefined, bTreatValuesAsAbsolute = false, fps = 60.0)
	{
		if(eAnimationType === undefined || eAnimationType === null)
			eAnimationType = AnimationType.EASE_IN_OUT;

		// 60fps timeout
		const iTimeoutPerFrame = 1000.0/fps;
		let iCurrSteps = 0;
		const iNumSteps = fDurationSeconds*1000.0/iTimeoutPerFrame;

		let origValues = {};
		let finalValues = {};
		let currValues = {};
		for(let propName in propValueMap)
		{
			if(pObject[propName].clone)
				origValues[propName] = pObject[propName].clone();
			else
				origValues[propName] = pObject[propName];

			if(bTreatValuesAsAbsolute)
				finalValues[propName] = propValueMap[propName];
			else
			{
				if(pObject[propName].add)
					finalValues[propName] = pObject[propName].add(propValueMap[propName]);
				else
					finalValues[propName] = pObject[propName] + propValueMap[propName];
			}
		}

		if(iNumSteps <= 0)
		{
			if(pCallback)
				pCallback.call(this, finalValues, 1.0, true, 1, 1);
			return null;
		}

		let pInterval = setInterval(() => {

			let fInterpValue = iCurrSteps/iNumSteps;
			iCurrSteps++;
			if(fInterpValue >= 1.0)
			{
				fInterpValue = 1.0;
				clearInterval(pInterval);
				pInterval = null;
			}

			if(eAnimationType == AnimationType.EASE_IN_OUT)
				fInterpValue = this.convertLinearToEaseInOutInterpolation(fInterpValue);

			for(let propName in propValueMap)
			{
				if(origValues[propName] instanceof BABYLON.Vector3)
					currValues[propName] = BABYLON.Vector3.Lerp(origValues[propName], finalValues[propName], fInterpValue);
				else
					currValues[propName] = this.interpolate(fInterpValue, origValues[propName], finalValues[propName]);
			}

			if(pCallback)
				pCallback.call(this, currValues, fInterpValue, !pInterval, iCurrSteps, iNumSteps);

		}, iTimeoutPerFrame);

		return pInterval;
	}

	getImageSource(strOrigSrc, thumbWidth, thumbHeight, bIncludeParamsInUrl = true, strOptFillMode='fill', format = null, bUseDevicePixelRatio = false)
	{
		let imgSrc = strOrigSrc;
		if(thumbWidth && thumbHeight && imgSrc)
		{
			let strSearchString = 'amazonaws.com/';
			let iEndIdx = strOrigSrc.indexOf(strSearchString);
			if(iEndIdx > 0)
			{
				// In some cases, we actually get URLs that also contain the bucket name - i.e. '/modsy/',
				// '/modsy-dev/', etc.; we intelligently strip those out, not relying on the the BE
				// to be consistent...
				let iModsyIdx = strOrigSrc.indexOf('/modsy', iEndIdx);
				if(iModsyIdx > 0)
				{
					// Find the next slash:
					iEndIdx = strOrigSrc.indexOf('/', iModsyIdx + 1);
					strSearchString = '/';
				}

				let strSuffix = strOrigSrc.substr(iEndIdx + strSearchString.length);
				imgSrc = 'https://cache.modsy.com/imgresize/';
				imgSrc += strSuffix;
			}

			if(bUseDevicePixelRatio)
			{
				thumbWidth *= window.devicePixelRatio;
				thumbHeight *= window.devicePixelRatio; 
			}

			if(bIncludeParamsInUrl)
			{
				imgSrc += "?w=" + thumbWidth + "&h=" + thumbHeight;
				if(format)
					imgSrc += '&fm=' + format;
				else
					imgSrc += '&fm=jpg';
				imgSrc += `&quality=${JPEG_QUALITY}&optimize=${DO_OPTIMIZE}`;
			}
		}
		return imgSrc;
	}

	onDragOver(ev)
	{
		ev.preventDefault();
	}

	receiveFile(ev)
	{
		ev.preventDefault();

		let funcContentsReader = (file) => {
			if(!file)
				return;
			let reader = new FileReader();

			if(file.type.indexOf('image/') >= 0)
			{
				// This is an image file
				reader.readAsDataURL(file);
				reader.onload = (evt) => {
					this.currDome.material.diffuseTexture = new BABYLON.Texture(evt.target.result, this.scene);
					this.onDomeTextureLoaded(this.currDome.material.diffuseTexture);
				}
			}
			else
			{
				// Assume this is a JSON VT data file.
				reader.readAsText(file, "UTF-8");
				reader.onload = (evt) => {
					this.load(evt.target.result);
				}
			}
		};

		if(ev.dataTransfer.items)
		{
			// Use DataTransferItemList interface to access the file(s)
			let file = null;
			[...ev.dataTransfer.items].forEach((item, i) => 
			{
				// If dropped items aren't files, reject them
				if(!file && item.kind === 'file')
					file = item.getAsFile();
			});
			funcContentsReader(file);
		}
		else
		{
			// Use DataTransfer interface to access the file(s)
			[...ev.dataTransfer.files].forEach((file, i) =>
			{
				funcContentsReader(file);
			});
		}
	}

	getDeviceType() 
	{
		const ua = navigator.userAgent;
		if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua))
			return 'tablet';
		else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua))
			return 'mobile';

		return 'desktop';
	}

	getZones()
	{
		if(!this.panoData['zones'])
			return [];

		return this.panoData['zones'].map((info) => { return info['name']; });
	}

	getRoomsForZone(strZoneName)
	{
		if(!this.panoData['zones'])
			return [];

		let zoneInfo = this.panoData['zones'].filter((info) => { return info.name === strZoneName; })[0];
		if(!zoneInfo)
			return [];

		return zoneInfo['rooms'].map((info) => { return info['name']; });
	}

	getCurrentRoomName()
	{
		if(!this.panoData['zones'])
			return '';

		for(let info of this.panoData['zones'])
		{
			if(!info['rooms'])
				continue;

			for(let roomInfo of info['rooms'])
			{
				if(roomInfo['cameras'] && roomInfo['cameras'].indexOf(this.currCameraInfo['name']) >= 0)
					return roomInfo['name'];
			}
		}

		return '';
	}

	getZoneInfoForCamera(strCamName = null)
	{
		if(!this.panoData['zones'])
			return '';

		if(!strCamName)
			strCamName = this.currCameraInfo['name'];

		let zoneInfo = this.panoData['zones'].filter((info) => { 
			return info['cameras'] && info['cameras'].indexOf(strCamName) >= 0; 
		})[0];

		return zoneInfo;
	}

	getCurrentZoneName()
	{
		const zoneInfo = this.getZoneInfoForCamera(this.currCameraInfo['name']);
		return zoneInfo ? zoneInfo['name'] : '';
	}

	getCurrentCamera() {
		return this.currCameraInfo;
	}

	moveToRoom(strRoomName, bResetToOriginalRotation = false)
	{
		for(let info of this.panoData['zones'])
		{
			if(!info['rooms'])
				continue;

			for(let roomInfo of info['rooms'])
			{
				if(roomInfo['name'] == strRoomName && roomInfo['cameras'] && roomInfo['cameras'].length > 0)
				{
					const strCameraName = roomInfo['startCameraName'] ? roomInfo['startCameraName'] : roomInfo['cameras'][0];
					return this.moveToCamera(strCameraName, bResetToOriginalRotation);
				}
			}
		}

		return false;
	}

	moveToZone(strZoneName, bResetToOriginalRotation = false)
	{
		for(let info of this.panoData['zones'])
		{
			if(info.name === strZoneName && info.cameras.length > 0)
			{
				const strCameraName = info['startCameraName'] ? info['startCameraName'] : info['cameras'][0];
				return this.moveToCamera(strCameraName, bResetToOriginalRotation);
			}
		}

		return false;
	}

	getPropInfo(propId)
	{
		if(!this.panoData['props'])
			return null;

		for(let propInfo of this.panoData['props'])
		{
			if(propInfo['id'] == propId)
				return propInfo;
		}

		return null;
	}

	getProductsForCameras(camerNames)
	{
		let productList = [];
		let idMap = {};
		for(let camName of camerNames)
		{
			let cameraInfo = this.getCameraInfo(camName);
			if(!cameraInfo || !cameraInfo['hotspots'])
				continue;

			for(let hotspotInfo of cameraInfo['hotspots'])
			{
				let propInfo = this.getPropInfo(hotspotInfo['id']);
				if(!propInfo)
					continue;

				if(idMap[propInfo['productId']])
					continue;

				idMap[propInfo['productId']] = true;
				productList.push(propInfo);
			}
		}

		return productList;
	}

	getSceneData()
	{
		let pRes = [];
		if(!this.panoData['zones'])
			return pRes;

		const strCurrRoomName = this.getCurrentRoomName();
		const strCurrZoneName = this.getCurrentZoneName();
		for(let currZoneInfo of this.panoData['zones'])
		{
			let resZoneInfo = {};
			if(currZoneInfo['rooms'])
			{
				resZoneInfo['rooms'] = currZoneInfo['rooms'].map((info) => { return {...info}; });

				for (let roomInfo of resZoneInfo['rooms'])
				{
					roomInfo['isCurrent'] = (roomInfo['name'] === strCurrRoomName);
					roomInfo['perCameraProducts'] = {};
					for(let camName of roomInfo['cameras'])
						roomInfo['perCameraProducts'][camName] = this.getProductsForCameras([camName]);
				}					
			}
			resZoneInfo['cameras'] = [...currZoneInfo['cameras']];
			resZoneInfo['name'] =  currZoneInfo['name'];
			resZoneInfo['isCurrent'] = (currZoneInfo['name'] === strCurrZoneName);

			resZoneInfo['perCameraProducts'] = {};
			for(let camName of resZoneInfo['cameras'])
				resZoneInfo['perCameraProducts'][camName] = this.getProductsForCameras([camName]);
			
			if(currZoneInfo['type'] === undefined)
				resZoneInfo['type'] = 'interior';
			else
				resZoneInfo['type'] = currZoneInfo['type'];

			resZoneInfo['isExterior'] = (resZoneInfo['type'] !== 'interior');

			pRes.push(resZoneInfo);
		}

		return pRes;
	}

	getStartCameraName()
	{
		return this.panoData['startCameraName'];
	}

	hasAssets() {
		return this.panoData['assets'] && this.panoData['assets'].length > 0;
	}

	static getCubemapUrl(assets, cameraName, resolution) {
		// assets are stored with sanitized camera names
		// this sanitization matches the one in the renderer
		const cleanCameraName = cameraName.replace(/[^a-zA-Z0-9-_]/g, '');
		const asset = assets.find((asset) => {
			return asset.cameraName == cleanCameraName &&
			asset.type == ResultAssetType.Cubemap &&
			asset.resolution == resolution
		});
		if (!asset) {
			throw new Error(`Cubemap for camera ${cleanCameraName} and resolution ${resolution} not found.`);
		}
		return asset.url
	}
};

window.createPlayer = (strCanvasId) => { return new Player(strCanvasId); };